import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAlerts } from "../../contexts/AlertsContext";
import { useApiClient } from "../../contexts/ApiClientContext";
import CHElement from "../../model/CHElement";
import APIEndPoints from "../../utils/APIEndPoints";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import AppSubSections from "../../utils/AppSubSections";
import ButtonInfo from "../common/button/ButtonInfo";
import AppIcons from "../../utils/AppIcons";
import Loading from "../common/Loading";
import DialogWindowType from "../common/dialog_window/DialogWindowType";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import { IApiClientError } from "../../shared/ApiClient";
import ButtonType from "../common/button/ButtonType";
import e3dModel from "../../model/e3dModel";
import Site from "../../model/Site";
import _3dModelsList from "../_3d_instances/_3dInstancesList";
import ImgUtil from "../../utils/ImgUtil";
import TimeUtil from "../../utils/TimeUtil";
import ConditionReportsList from "../condition_reports/CondtionReportsList";
import EntityStats from "../../model/EntityStats";
import ChemiNovaEntityType from "../../model/enums/ChemiNovaEntityType";
import { Tooltip } from "react-tooltip";
import MeasurementUnitsUtil from "../../utils/MeasurementUnitsUtil";
import AnnexDataList from "../annexdata/AnnexDataList";
import PagedData from "../../model/PagedData";
import ConditionReport from "../../model/ConditionReport";
import EmbeddingToolInfo from "../../utils/EmbeddingToolInfo";
import UserRoles from "../../model/enums/UserRole";
import { useAuth } from "../../contexts/AuthContext";
import ReportButton from "../common/ReportButton";

function E3dDetails() {
  // read the ID from the url
  const { id } = useParams();

  const authManager = useAuth();
  // manage loading animation
  const [loading, setLoading] = useState(true);
  // set the data
  const [data, setData] = useState<e3dModel>(e3dModel.EmptyInstance());
  // set the state to track any error messages received from the server
  const [errorMessage, setErrorMessage] = useState("");
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the alerts system
  const alertsManager = useAlerts();

  // load record data from the server
  useEffect(() => {
    window.scrollTo(0, 0);

    const getAllData = async () => {
      try {
        let e3d: e3dModel | undefined = undefined;
        if (id) {
          e3d = await apiClient?.get<e3dModel>({
            url: APIEndPoints.E3DS.ONE(parseInt(id)),
          });
          // add information about the condition reports
          const condRep = await apiClient?.get<PagedData<ConditionReport>>({
            url: APIEndPoints.CONDITION_REPORTS.ALL(parseInt(id)),
          });
          if (
            condRep &&
            Array.isArray(condRep.items) &&
            condRep.items.length > 0
          ) {
            e3d.condition_report = condRep.items[0];
          }
          setData(e3d);
        }
      } catch (error: any) {
        if (!error.processed) {
          setErrorMessage("Something went wrong. Try again later!");
        }
      }
      setLoading(false);
    };
    getAllData();
  }, [apiClient, id]);

  const handleDelete = () => {
    apiClient
      ?.delete({
        url: APIEndPoints.E3DS.ONE(parseInt(id!)),
      })
      .then((response) => {
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "e3D record deleted successfully",
        });
        navigate(
          SETTINGS.get_path({
            section: AppSections.CHELEMENTS,
            id: data.id_chelement,
            subsection: AppSubSections.VIEW,
          }),
        );
      })
      .catch((error: IApiClientError) => {
        if (!error.processed) {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Unable to delete the e3D record",
          });
        }
      });
  };

  const unitShort = MeasurementUnitsUtil.short(data.scale);

  if (loading) {
    return <Loading />;
  }

  const canEdit =
    !EmbeddingToolInfo.isChemiTool() &&
    (data.id_user === authManager.getUserID() || authManager.isAdmin());

  const canAddData =
    !EmbeddingToolInfo.isChemiTool() &&
    (authManager.isAdmin() ||
      authManager.isSpecialist() ||
      authManager.isBoardMember());

  return (
    <div className="details-page">
      <div className="details-header">
        {/* Image */}
        <div className="details-icon">
          <div>
            <img
              src={
                ImgUtil.display_icon(data.icon_path) ||
                process.env.REACT_APP_PUBLIC_URL +
                  "/resources/images/no-image.png"
              }
              alt="e3D icon"
            />
          </div>
        </div>

        {/* Info Section */}
        <div className="details-header-content">
          <div className="details-title">
            <h1>{data.name}</h1>
          </div>
          <div className="details-overview">
            <ButtonInfo className="span-3">
              {AppIcons.USER}
              created by: <span>{data.username}</span>
            </ButtonInfo>
            <ButtonInfo className="span-3">
              {AppIcons.GUID} GUID: <span>{data.GUID}</span>
            </ButtonInfo>
            <ButtonInfo>
              {AppIcons.CLOCK}
              reference date: {TimeUtil.display_date(data.reference_date)}
            </ButtonInfo>
            <div></div>
            <div></div>
            <ButtonInfo>
              {AppIcons.E3D}
              <span>
                {EntityStats.getStats(
                  data.counts,
                  ChemiNovaEntityType._3DINSTANCE,
                )}
              </span>
              3D scans
            </ButtonInfo>
            <ButtonInfo>
              {AppIcons.ANNOTATIONS}
              <span>
                {EntityStats.getStats(
                  data.counts,
                  ChemiNovaEntityType.ANNOTATION,
                )}
              </span>
              annotations
            </ButtonInfo>
            <ButtonInfo>
              {AppIcons.ALTERATION_FORMS}
              <span>
                {EntityStats.getStats(
                  data.counts,
                  ChemiNovaEntityType.ALTERATION_EVENT,
                )}
              </span>
              alteration events
            </ButtonInfo>
          </div>

          <div className="flex items-start mt-5">
            {canEdit && (
              <Button
                label="Delete this record"
                purpose={ButtonAction.DELETE}
                onClick={() => {
                  alertsManager.showDialogWindow({
                    type: DialogWindowType.CONFIRM,
                    message: "Are you sure you want to delete this record?",
                    cancelLabel: "Cancel",
                    confirmLabel: "Yes, delete record",
                    onConfirm: handleDelete,
                  });
                }}
              />
            )}

            <ReportButton
                entityType="E3D"
                entityName={data.name}
                entityId={data.id || 0}
            />
          </div>
        </div>
      </div>

      {/* Details Section */}
      <div className="details-section">
        <div className="details-section-title">
          <h2>Record details</h2>
          {canEdit && (
            <Button
              label="Edit information"
              purpose={ButtonAction.EDIT}
              type={ButtonType.ICON_TEXT}
              onClick={() =>
                navigate(
                  SETTINGS.get_path({
                    section: AppSections.E3DS,
                    id: data.id,
                    subsection: AppSubSections.EDIT,
                  }),
                )
              }
            />
          )}
        </div>
        <p className="details-description">{data.description}</p>

        <div className="details-section-content">
          <table className="details-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Unit</td>
                <td>{MeasurementUnitsUtil.long(data.scale)}</td>
              </tr>
              <tr>
                <td>Width</td>
                <td>
                  {data.width ? data.width + " " + unitShort : "Not found"}
                </td>
              </tr>
              <tr>
                <td>Length</td>
                <td>
                  {data.length ? data.length + " " + unitShort : "Not found"}
                </td>
              </tr>
              <tr>
                <td>Height</td>
                <td>
                  {data.height ? data.height + " " + unitShort : "Not found"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* 3D models section */}
      <div className="details-section">
        <div className="details-section-title">
          <h2>
            3D Scans
            <span className="tooltip-hint">
              <a data-tooltip-id="e3d-tooltip" data-tooltip-place="right">
                {AppIcons.INFO}
              </a>
              <Tooltip id="e3d-tooltip">
                Refers to a 3D representation of a cultural
                <br />
                heritage asset created through scans performed
                <br /> with different sensors.
              </Tooltip>
            </span>
          </h2>
          {canAddData && (
            <Button
              label="Add 3D Scan"
              purpose={ButtonAction.CREATE}
              type={ButtonType.ICON_TEXT}
              onClick={() =>
                navigate(
                  SETTINGS.get_path({
                    section: AppSections.E3DS,
                    id: data.id,
                    additionalSections: [AppSections._3DINSTANCES],
                    subsection: AppSubSections.CREATE,
                  }),
                )
              }
            />
          )}
        </div>
        <div className="details-section-content">
          <_3dModelsList />
        </div>
      </div>

      {/* Condition Reports section */}
      <div className="details-section">
        <div className="details-section-title">
          <h2>Condition Report</h2>
          {!data.condition_report && canEdit && (
            <Button
              label="Create Condition Report"
              purpose={ButtonAction.CREATE}
              type={ButtonType.ICON_TEXT}
              onClick={() =>
                navigate(
                  SETTINGS.get_path({
                    section: AppSections.E3DS,
                    id: data.id,
                    additionalSections: [
                      `${data.id_chelement}/condition-reports`,
                    ],
                    subsection: AppSubSections.CREATE,
                  }),
                )
              }
            />
          )}
        </div>
        <div className="details-section-content">
          <ConditionReportsList itemSelectionMode={false} />
        </div>
      </div>

      {/* Documents Section */}
      <div className="details-section">
        <div className="details-section-title">
          <h2>
            Documentation
            <span className="tooltip-hint">
              <a data-tooltip-id="my-tooltip" data-tooltip-place="right">
                {AppIcons.INFO}
              </a>
              <Tooltip id="my-tooltip">
                You can upload general documents such as archive documents,
                <br />
                old photos, territorial context document, articles, etc. <br />
                <br />
                Please select the most representative documents
                <br /> as the maximum space allowed is 10GB.
              </Tooltip>
            </span>
          </h2>
          {canAddData && (
            <Button
              label="Add document"
              purpose={ButtonAction.CREATE}
              type={ButtonType.ICON_TEXT}
              onClick={() =>
                navigate(
                  SETTINGS.get_path({
                    section: AppSections.E3DS,
                    id: data.id,
                    additionalSections: [AppSections.ANNEXDATA],
                    subsection: AppSubSections.CREATE,
                  }),
                )
              }
            />
          )}
        </div>
        <div className="details-section-content">
          <AnnexDataList
            id_chelement={undefined}
            id_site={undefined}
            id_e3dmodel={data.id}
          />
        </div>
      </div>
    </div>
  );
}

export default E3dDetails;
